/**
 * Block 13 PR-G Phase 5B — Presenter Экрана 2.13.3 «Готовый PDF» (ADR-0005).
 *
 * Pure presenter — renders layout shell (P-046) + PDF preview embed + 4
 * vertical CTAs (P-019). **NO VideoButton** (child screen — entry это 2.13.1
 * per P-040).
 *
 * **PDF embed strategy:** `<object data={blobUrl} type="application/pdf">` —
 * native browser PDF viewer. Safari/iOS не поддерживают `<object>` для PDF
 * inline rendering — fallback к `<iframe>` через nested children pattern.
 * Если оба fail (no PDF plugin installed) — fallback message + «Открыть
 * полноэкранно» CTA остаётся доступным как escape hatch.
 *
 * **4 vertical CTAs** (P-019 — горизонтальные ряды запрещены):
 *   1. «Скачать PDF» (primary, bg-cta) — trigger download.
 *   2. «Переслать клиенту» (primary, bg-cta) — opens ShareContractBottomSheet.
 *   3. «Открыть полноэкранно» (secondary, bg-card + border-accent) —
 *      `<a href={blobUrl} target="_blank">` опция для просмотра вне страницы.
 *   4. «Вернуться к редактированию» (tertiary, bg-card + border-accent) —
 *      Link к `/contracts/:id/edit`. Phase 6 wires route; Phase 5 — non-rendered.
 *
 * **Design system (P-038..P-048):**
 *   - bg-canvas main + bg-card preview container (P-038)
 *   - text-ink labels (P-039)
 *   - bg-cta только primary actions (Скачать + Поделиться) — P-041 button allowlist
 *   - secondary CTAs: bg-card + border-[1.5px] border-accent + text-ink
 *   - icons size=22 via lucide-react (Download / Send / Maximize2 / Edit3)
 *   - min-h-tap dimensions (P-044)
 *
 * **Loading / error states** (P-021):
 *   - status='loading' — pre-render spinner («Готовим PDF…»)
 *   - status='ready' — embed visible + 4 CTAs enabled
 *   - status='not-found' — «Договор не найден» + back link
 *   - status='error' — render error + retry CTA
 *
 * Reference:
 *   - PRD §10.13.3 «Экран 2.13.3 — Финальный документ»
 *   - ADR-0025 §Phase 5
 *   - `apps/web/src/features/contract/ContractFormView.tsx` (Phase 4A layout pattern)
 */

import { Link } from 'react-router-dom';
import { Download, Send, Maximize2, Edit3 } from 'lucide-react';

import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';

import type { SavedContract } from './state/savedContractsIdb';

export type ContractPdfPreviewStatus = 'loading' | 'ready' | 'not-found' | 'error';

export interface ContractPdfPreviewViewProps {
  status: ContractPdfPreviewStatus;
  contract?: SavedContract;
  /** Blob URL для embed + «Открыть полноэкранно». null до status='ready'. */
  blobUrl?: string | null;
  /** Download trigger — uses already-rendered blob (no re-render). */
  onDownload?: () => void;
  /** Opens ShareContractBottomSheet. */
  onShareOpen?: () => void;
  /** Retry render after error. */
  onRetry?: () => void;
  /** PR-B1 (ADR-0034) — session-end clear hook, прокидывается в BottomBar «На главную». */
  onBeforeNavigateHome?: () => void;
}

export function ContractPdfPreviewView({
  status,
  contract,
  blobUrl,
  onDownload,
  onShareOpen,
  onRetry,
  onBeforeNavigateHome,
}: ContractPdfPreviewViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Готовый договор" />

      <section
        className="flex-1 overflow-y-auto pb-6 pt-3 flex flex-col gap-4"
        aria-label="Превью договора"
      >
        {status === 'loading' ? (
          <p
            role="status"
            data-testid="contract-pdf-loading"
            className="text-ink/60 text-center py-12 mx-3"
          >
            Готовим PDF…
          </p>
        ) : null}

        {status === 'not-found' ? (
          <div
            role="status"
            data-testid="contract-pdf-not-found"
            className="mx-3 bg-card border-[1.5px] border-accent rounded-2xl px-5 py-10 text-center"
          >
            <p className="text-ink text-base m-0">Договор не найден.</p>
            <p className="text-ink/60 text-sm mt-2 m-0">
              Возможно, он был удалён или ссылка устарела.
            </p>
            <Link
              to="/contracts"
              data-testid="contract-pdf-back-to-list"
              aria-label="Вернуться к списку договоров"
              className="inline-block mt-4 min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 no-underline"
            >
              К списку договоров
            </Link>
          </div>
        ) : null}

        {status === 'error' ? (
          <div className="mx-3 flex flex-col items-center gap-3 py-8">
            <p
              role="alert"
              data-testid="contract-pdf-error"
              className="text-ink text-base text-center m-0"
            >
              Не удалось создать PDF. Попробуйте ещё раз.
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                aria-label="Повторить создание PDF"
                data-testid="contract-pdf-retry"
                className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
              >
                Повторить
              </button>
            ) : null}
          </div>
        ) : null}

        {status === 'ready' && blobUrl ? (
          <>
            <div
              className="mx-3 bg-card border-[1.5px] border-accent rounded-2xl overflow-hidden"
              data-testid="contract-pdf-preview-container"
            >
              {/*
                <object> rendering предпочтительнее (full browser PDF viewer),
                <iframe> fallback автоматически если плагин не поддерживает inline
                PDF (Safari iOS). Tag nesting — стандартный graceful degradation.
              */}
              <object
                data={blobUrl}
                type="application/pdf"
                className="w-full h-[60vh]"
                data-testid="contract-pdf-object"
                aria-label="Превью PDF договора"
              >
                <iframe
                  src={blobUrl}
                  title="PDF договора"
                  className="w-full h-[60vh] border-0"
                  data-testid="contract-pdf-iframe"
                >
                  <p className="text-ink p-4">
                    Не удалось отобразить PDF в браузере. Используйте «Открыть полноэкранно» или
                    «Скачать PDF».
                  </p>
                </iframe>
              </object>
            </div>

            <div className="mx-3 flex flex-col gap-2.5" data-testid="contract-pdf-actions">
              <button
                type="button"
                onClick={onDownload}
                aria-label="Скачать PDF"
                data-testid="contract-pdf-download-button"
                className="w-full min-h-tap rounded-2xl bg-cta text-ink text-base font-semibold flex items-center justify-center gap-3 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
              >
                <Download size={22} aria-hidden="true" />
                Скачать PDF
              </button>

              <button
                type="button"
                onClick={onShareOpen}
                aria-label="Переслать договор клиенту"
                data-testid="contract-pdf-share-button"
                className="w-full min-h-tap rounded-2xl bg-cta text-ink text-base font-semibold flex items-center justify-center gap-3 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
              >
                <Send size={22} aria-hidden="true" />
                Переслать клиенту
              </button>

              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Открыть PDF полноэкранно в новой вкладке"
                data-testid="contract-pdf-fullscreen-link"
                className="w-full min-h-tap rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 no-underline"
              >
                <Maximize2 size={22} aria-hidden="true" />
                Открыть полноэкранно
              </a>

              {contract ? (
                <Link
                  to={`/contracts/${contract.id}/edit`}
                  aria-label="Вернуться к редактированию договора"
                  data-testid="contract-pdf-edit-link"
                  className="w-full min-h-tap rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 no-underline"
                >
                  <Edit3 size={22} aria-hidden="true" />
                  Вернуться к редактированию
                </Link>
              ) : null}
            </div>
          </>
        ) : null}
      </section>

      <BottomBar variant="default" onBeforeNavigateHome={onBeforeNavigateHome} />
    </main>
  );
}
